import { IListColumnConfig } from '@datachain/ui-sdk/common';
import { Map } from 'immutable';

import dayjs from '../configs/dayjs';
import {
	ColumnCompositeFilterExpr,
	CompositeFilterExpr,
	DxFilterField,
	DxSortField,
	isColumnCompositeFilter,
	isCompositeFilter,
	isSimpleFilter,
	SimpleFilterExp,
} from '../dx-types';
import { Operator } from './operator-mapping.util';

export class DxGridUtil {
	public static parseFilters<RowAttr = Record<string, unknown>>(
		filter:
			| SimpleFilterExp<RowAttr>
			| ColumnCompositeFilterExpr<RowAttr>
			| CompositeFilterExpr<RowAttr>,
		extracted: Array<DxFilterField<RowAttr>> = []
	): Array<DxFilterField<RowAttr>> {
		const out = extracted;
		if (isSimpleFilter<RowAttr>(filter)) {
			const [column, op, val] = filter;
			if (!Array.isArray(val) && val !== undefined && val !== null) {
				if (typeof val === 'string' && val.trim() !== '') {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					out.push({
						field: column,
						operator: op,
						value: val,
					});
				} else if (typeof val !== 'string') {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					out.push({
						field: column,
						operator: op,
						value: val,
					});
				}
			} else {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				out.push({
					field: column,
					operator: op,
					value: val,
				});
			}
		} else if (isColumnCompositeFilter<RowAttr>(filter)) {
			const [columnLeft, , columnRight] = filter;
			DxGridUtil.parseFilters(columnLeft, out);
			DxGridUtil.parseFilters(columnRight, out);
		} else if (isCompositeFilter<RowAttr>(filter)) {
			const [compositeLeft, , compositeRight] = filter;
			DxGridUtil.parseFilters(compositeLeft, out);
			DxGridUtil.parseFilters(compositeRight, out);
		}
		return out;
	}

	public static generateSortFields<T>(
		columns: Map<string, IListColumnConfig<T>>,
		action: string,
		sortOrder: 'asc' | 'desc'
	): Array<DxSortField> {
		const columnNumber = action.match(/columns\[(\d+)\]/);
		const index = columnNumber ? parseInt(columnNumber[1], 10) : null;
		const [field] =
			columns.findEntry((column) => column.visibleIndex === index) ?? [];
		const sortedFields: Array<DxSortField> = [];
		if (field) {
			sortedFields.push({
				selector: field,
				order: sortOrder,
			});
		}
		return sortedFields;
	}

	public static generateDateFilter(
		field: string,
		operator: Operator,
		selectedDate: Date | Array<Date>
	): Record<string, string> {
		const result: Record<string, string> = {};

		if (operator === Operator.Between) {
			const [selectedStartDate, selectedEndDate] = selectedDate as Array<Date>;

			const startDate = dayjs(selectedStartDate as Date);
			let endDate = startDate.add(1, 'year');

			if (selectedEndDate) {
				endDate = dayjs(selectedEndDate as Date).add(1, 'd');
			}

			result[`${field}.${Operator.GreaterThanOrEqual}`] =
				startDate.toISOString();
			result[`${field}.${Operator.LessThan}`] = endDate.toISOString();
			return result;
		}

		const startDate = dayjs(selectedDate as Date);
		const endDate = startDate.add(1, 'day');
		switch (operator) {
			case Operator.Equals: {
				result[`${field}.${Operator.GreaterThanOrEqual}`] =
					startDate.toISOString();
				result[`${field}.${Operator.LessThan}`] = endDate.toISOString();
				break;
			}
			case Operator.LessThan: {
				result[`${field}.${Operator.LessThan}`] = startDate.toISOString();
				break;
			}
			case Operator.LessThanOrEqual: {
				result[`${field}.${Operator.LessThanOrEqual}`] = endDate.toISOString();
				break;
			}
			case Operator.GreaterThan: {
				result[`${field}.${Operator.GreaterThan}`] = endDate.toISOString();
				break;
			}
			case Operator.GreaterThanOrEqual: {
				result[`${field}.${Operator.GreaterThanOrEqual}`] =
					startDate.toISOString();
				break;
			}
			default: {
				throw new Error(`Unrecognized Operator: ${operator}`);
			}
		}

		return result;
	}
}
